import { createContext, use, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { fetchUserAttributes } from "aws-amplify/auth";
import { useAuth } from "./AuthProvider.js";

interface EmailVerificationState {
  /** True until the first attribute fetch resolves. */
  loading: boolean;
  /** Whether the signed-in user's email is verified. */
  verified: boolean;
  /** The signed-in user's email, for display in the verify UI. */
  email: string | null;
  /** Re-pull attributes from Cognito (call after a successful verify). */
  refresh: () => Promise<void>;
}

const EmailVerificationContext = createContext<EmailVerificationState | null>(null);

/**
 * Tracks whether the current user's email is verified. Lives above the app
 * shell so both the persistent "verify your email" banner and the
 * `/app/verify-email` screen read one source of truth — verifying on the screen
 * calls `refresh()`, which clears the banner without a reload.
 *
 * `email_verified` comes from `fetchUserAttributes()` (live from Cognito) rather
 * than the ID-token claim, which only updates on token refresh — so it flips to
 * true the moment `confirmUserAttribute` succeeds.
 */
export function EmailVerificationProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const [loading, setLoading] = useState(true);
  // Default to verified so the banner never flashes before we know.
  const [verified, setVerified] = useState(true);
  const [email, setEmail] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const attrs = await fetchUserAttributes();
      setVerified(attrs.email_verified === "true");
      setEmail(attrs.email ?? null);
    } catch {
      // Signed out or attributes unavailable — don't nag.
      setVerified(true);
      setEmail(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (status === "signed-in") {
      void refresh();
    } else if (status === "signed-out") {
      setLoading(false);
      setVerified(true);
      setEmail(null);
    }
  }, [status, refresh]);

  const value = useMemo<EmailVerificationState>(
    () => ({ loading, verified, email, refresh }),
    [loading, verified, email, refresh],
  );

  return <EmailVerificationContext value={value}>{children}</EmailVerificationContext>;
}

export function useEmailVerification(): EmailVerificationState {
  const ctx = use(EmailVerificationContext);
  if (!ctx) {
    throw new Error("useEmailVerification must be used inside <EmailVerificationProvider>");
  }
  return ctx;
}
